import {
  ALLOWED_FILE_TYPES,
  MAXIMUM_FILE_UPLOAD_SIZE,
} from 'shared/constants/messages';

// File size limits for different channels (in MB)
const WHATSAPP_MAX_SIZE_MEDIA = 16; // images, audio, video
const WHATSAPP_MAX_SIZE_DOCUMENT = 100; // documents
const INSTAGRAM_MAX_SIZE_IMAGE = 8;
const INSTAGRAM_MAX_SIZE_VIDEO = 100;
const SMS_MAX_SIZE = 5; // MMS limit
const LINE_MAX_SIZE = 200;

// File types for different channels
const WHATSAPP_ALLOWED_TYPES =
  'image/jpeg,image/png,audio/aac,audio/mp4,audio/mpeg,audio/amr,audio/ogg,' +
  'video/mp4,video/3gpp,' +
  'application/pdf,application/vnd.ms-powerpoint,application/msword,' +
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document,' +
  'application/vnd.openxmlformats-officedocument.presentationml.presentation,' +
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,' +
  'application/vnd.ms-excel,text/plain';

// Instagram supports: image (jpeg, png, gif), video (mp4, quicktime)
// Audio is also supported but only in specific formats (aac, m4a, wav, mp4)
// We allow common audio types here and convert unsupported ones to WAV in fileUploadMixin
const INSTAGRAM_ALLOWED_TYPES =
  'image/jpeg,image/png,image/gif,video/mp4,video/quicktime,' +
  'audio/aac,audio/mp4,audio/m4a,audio/wav,audio/mpeg,audio/mp3,audio/ogg,audio/webm';

const FACEBOOK_ALLOWED_TYPES =
  'image/*,video/*,audio/*,application/pdf';

const SMS_ALLOWED_TYPES = 'image/jpeg,image/png,image/gif';

const LINE_ALLOWED_TYPES = 'image/jpeg,image/png,video/mp4,audio/m4a';

/**
 * Get allowed file types based on channel type and medium
 * @param {Object} options
 * @param {string} options.channelType - The type of channel
 * @param {string} options.medium - The medium (optional)
 * @returns {string} Comma-separated list of allowed MIME types
 */
export function getAllowedFileTypesByChannel({ channelType, medium } = {}) {
  if (!channelType) {
    return ALLOWED_FILE_TYPES;
  }

  const normalizedType = channelType.toLowerCase();

  // WhatsApp channels
  if (
    normalizedType.includes('whatsapp') ||
    normalizedType.includes('channel::whatsapp')
  ) {
    return WHATSAPP_ALLOWED_TYPES;
  }

  // Instagram channels
  if (
    normalizedType.includes('instagram') ||
    normalizedType.includes('channel::instagram')
  ) {
    return INSTAGRAM_ALLOWED_TYPES;
  }

  // Facebook channels
  if (
    normalizedType.includes('facebook') ||
    normalizedType.includes('channel::facebook')
  ) {
    return FACEBOOK_ALLOWED_TYPES;
  }

  // SMS/Twilio channels
  if (
    normalizedType.includes('sms') ||
    normalizedType.includes('twilio') ||
    normalizedType.includes('channel::sms') ||
    normalizedType.includes('channel::twilio')
  ) {
    return SMS_ALLOWED_TYPES;
  }

  // Line channel
  if (
    normalizedType.includes('line') ||
    normalizedType.includes('channel::line')
  ) {
    return LINE_ALLOWED_TYPES;
  }

  // Default - all file types
  return ALLOWED_FILE_TYPES;
}

/**
 * Get maximum upload size based on channel type and mime type
 * @param {Object} options
 * @param {string} options.channelType - The type of channel
 * @param {string} options.medium - The medium (optional)
 * @param {string} options.mime - The MIME type of the file
 * @returns {number} Maximum file size in MB
 */
export function getMaxUploadSizeByChannel({ channelType, medium, mime } = {}) {
  if (!channelType) {
    return MAXIMUM_FILE_UPLOAD_SIZE;
  }

  const normalizedType = channelType.toLowerCase();
  const isDocument =
    mime &&
    (mime.includes('application/') ||
      mime.includes('text/') ||
      mime === 'application/pdf');
  const isVideo = mime && mime.includes('video/');
  const isImage = mime && mime.includes('image/');

  // WhatsApp channels
  if (
    normalizedType.includes('whatsapp') ||
    normalizedType.includes('channel::whatsapp')
  ) {
    return isDocument ? WHATSAPP_MAX_SIZE_DOCUMENT : WHATSAPP_MAX_SIZE_MEDIA;
  }

  // Instagram channels
  if (
    normalizedType.includes('instagram') ||
    normalizedType.includes('channel::instagram')
  ) {
    return isVideo ? INSTAGRAM_MAX_SIZE_VIDEO : INSTAGRAM_MAX_SIZE_IMAGE;
  }

  // SMS/Twilio channels
  if (
    normalizedType.includes('sms') ||
    normalizedType.includes('twilio') ||
    normalizedType.includes('channel::sms') ||
    normalizedType.includes('channel::twilio')
  ) {
    return SMS_MAX_SIZE;
  }

  // Line channel
  if (
    normalizedType.includes('line') ||
    normalizedType.includes('channel::line')
  ) {
    return LINE_MAX_SIZE;
  }

  // Default
  return MAXIMUM_FILE_UPLOAD_SIZE;
}

export default getAllowedFileTypesByChannel;
